import { loadShips } from "../../data/loadShips";
import type { NpcHullTier, PlayerShip } from "../../types";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { startingFuelCapacity } from "../../fuel";
import { startingProvisionsCapacity } from "../../provisions";
import { effectiveNow } from "../../gameClock";

const ships = loadShips();

// A new captain picks from Tier 1 only (Don's direction). This screen hands
// over a ship for FREE -- it is the starting hull, not a purchase -- so
// offering the whole roster let a captain begin in a T4 hull worth millions
// of credits and skip the entire progression the economy is built around.
// Higher tiers are earned through the Shipyard, which charges for them.
const STARTER_TIER: NpcHullTier = "T1";
const starterShips = ships.filter((s) => s.hullTier === STARTER_TIER);
const ROLES = [...new Set(starterShips.map((s) => s.role))];

export function renderShipSelectScreen(container: HTMLElement, ctx: ScreenContext): void {
  const wrap = document.createElement("div");

  const filtered = starterShips.filter((s) => state.roleFilter === "All" || s.role === state.roleFilter);

  wrap.innerHTML = `
    <h1>Select Your Ship</h1>
    <p class="dim">
      Your first command, on the Guild's account. ${starterShips.length} Tier 1 hulls across
      ${ROLES.length} roles &mdash; pick the job you want to start with, not the biggest ship.
      Heavier hulls are bought at a Station Shipyard once you have the credits.
    </p>

    <div class="filter-row">
      <label>
        Role
        <select data-filter="role">
          <option value="All" ${state.roleFilter === "All" ? "selected" : ""}>All</option>
          ${ROLES.map((r) => `<option value="${r}" ${state.roleFilter === r ? "selected" : ""}>${r}</option>`).join("")}
        </select>
      </label>
    </div>

    <div class="ship-list">
      ${filtered.map((s) => shipCardHtml(s)).join("")}
    </div>
  `;

  wrap.querySelector<HTMLSelectElement>('select[data-filter="role"]')!.addEventListener("change", (e) => {
    state.roleFilter = (e.target as HTMLSelectElement).value;
    ctx.rerender();
  });
  // No Hull Tier control: the roster here is Tier 1 by definition, so a filter
  // over it would offer exactly one meaningful option. state.tierFilter still
  // exists and is still used by the Shipyard, where all four tiers are real.

  wrap.querySelectorAll<HTMLElement>(".ship-card").forEach((card) => {
    card.addEventListener("click", () => {
      const name = card.dataset.name!;
      const variant = card.dataset.variant!;
      state.currentShip = ships.find((s) => s.name === name && s.variant === variant) ?? null;
      state.currentShipInstanceId = state.currentShip ? crypto.randomUUID() : null;
      state.fuelCapacity = state.currentShip ? startingFuelCapacity(state.currentShip.hullTierLabel) : 0;
      state.fuelOnBoard = state.fuelCapacity;
      state.provisionsCapacity = state.currentShip ? startingProvisionsCapacity(state.currentShip.crew) : 0;
      state.provisionsOnBoard = state.provisionsCapacity;
      state.provisionsLastUpdatedAt = effectiveNow(state.clock, Date.now());
      state.isLoanerShip = false;
      // A ship must have a real fleet entry to track its own last-docked
      // Base (16.62's dockedAtBaseKey) -- the very first (starter) ship
      // was never pushed into state.fleet before, closing that gap here.
      if (state.currentShip && state.currentShipInstanceId) {
        state.fleet.push({ id: state.currentShipInstanceId, ship: state.currentShip, source: "owned", stance: "Active" });
      }
      triggerSave(ctx); // ship selection is a key state change (16.34-adjacent save policy)
      ctx.navigate("board");
    });
  });

  container.appendChild(wrap);
}

function shipCardHtml(s: PlayerShip): string {
  const ehp = s.hullHp + s.shieldHp;
  return `
    <div class="ship-card" data-name="${s.name}" data-variant="${s.variant}">
      <div class="ship-card-title">
        <strong>${s.name}</strong> <span class="pill">${s.variant}</span> <span class="pill">${s.hullTier}</span>
      </div>
      <div class="ship-card-stats dim">
        EHP ${ehp} &middot; DPS ${s.dps} &middot; Cargo ${s.cargoM3} m3 · Ore ${s.oreHoldM3} m3 &middot; Crew ${s.crew} &middot; ${s.costCr} CR
      </div>
    </div>
  `;
}
